import { GetFormControlModule } from '@abovo/shared-angular-utils';
import { IntermediateReport, TeamTarget } from '@abovo/shared-models';
import {
   AvatarComponent,
   ComplexTableCellContext,
   ComplexTableCellTemplates,
   ComplexTableColumn,
   ComplexTableComponent,
   InternalOrderProductDisplayComponent,
   MarkdownPipe,
   NumberInputComponent,
} from '@abovo/shared-ui-components';
import { CategoryGroupKey, HIDDEN_RATE_TYPES, SPECIAL_KPIS, SPECIAL_KPI_VALUE_TYPES, findInternalOrderCategory, isDefined } from '@abovo/shared-utils';
import {
   ActualKeyPerformanceIndicatorStore,
   LookupFacadeService,
   findOrderMediaDistributions,
   primaryKpi,
   sameTarget,
   specialistOfDiscipline,
} from '@abovo/webvis-data-access';
import { CommonModule } from '@angular/common';
import {
   ChangeDetectionStrategy,
   Component,
   OnDestroy,
   Signal,
   TemplateRef,
   WritableSignal,
   computed,
   effect,
   inject,
   input,
   output,
   signal,
   untracked,
   viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatGridListModule } from '@angular/material/grid-list';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslatePipe } from '@ngx-translate/core';
import { differenceInCalendarDays } from 'date-fns';
import { NgStringPipesModule } from 'ngx-pipes';
import { Subject, merge } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { DisciplineIconPipe } from '../../../../pipes/discipline-icon.pipe';
import { SecondaryKpiEntry } from '../../../proposal-page/components/secondary-kpis/secondary-kpis.component';
import { useSecondaryKpiDialog } from '../../../proposal-page/hooks/use-secondary-kpi-dialog.hook';
import { ProposalPageModule } from '../../../proposal-page/proposal-page.module';
import { IntermediateReportRow, KpiForm, KpiFormGroup } from '../../models/intermediate-report.models';
import { FormHelperService } from '../../services/form-helper-service';
import { SaveLoadHelperService } from '../../services/save-load-helper.service';

type CellTemplate = TemplateRef<ComplexTableCellContext<IntermediateReportRow, KpiFormGroup>>;
type KpiFormValue = KpiFormGroup['value'];

interface Totals {
   investment: number;
   kpiData: Array<{
      kpislug: string;
      value: number | null;
      rate: number | null;
      rateTypeName: string;
      decimalPrecision?: number;
   }>;
}

@Component({
   selector: 'webvis-ui-intermediate-report-grid',
   templateUrl: 'intermediate-report-grid.component.html',
   styleUrl: 'intermediate-report-grid.component.scss',
   changeDetection: ChangeDetectionStrategy.OnPush,
   imports: [
      CommonModule,
      ComplexTableComponent,
      TranslatePipe,
      MatGridListModule,
      NumberInputComponent,
      GetFormControlModule,
      ProposalPageModule,
      FaIconComponent,
      AvatarComponent,
      MatIconButton,
      FormsModule,
      ReactiveFormsModule,
      NgStringPipesModule,
      InternalOrderProductDisplayComponent,
      DisciplineIconPipe,
      MarkdownPipe,
   ],
})
export class IntermediateReportGridComponent implements OnDestroy {
   private actualKpiStore = inject(ActualKeyPerformanceIndicatorStore);
   public formHelperService = inject(FormHelperService);
   private saveLoadHelperService = inject(SaveLoadHelperService, { optional: true });
   private lookupFacadeService = inject(LookupFacadeService);
   private secondaryKpiDialogHook = useSecondaryKpiDialog();

   columnConfig = input.required<Array<ComplexTableColumn>>();
   orders = input.required<Array<IntermediateReportRow>>();
   selectedView = input.required<'flights' | 'grouped' | 'totals'>();
   reportDate = input.required<Date>();
   intermediateReport = input.required<IntermediateReport>();
   formDataMap = input.required<Map<number, KpiForm>>();
   readonly = input(false);
   reportModeActive = input(false);
   showMediaBundles = input(true);

   /** The 'totals' view shows only the totals */
   rows = computed(() => (this.selectedView() === 'totals' ? [] : this.orders()));
   subRowsOf = (row: IntermediateReportRow): Array<KpiFormGroup> => this.formDataMap().get(row.id)?.formGroup.controls ?? [];

   /**
    * The flight-orders list cannot deliver the order's campaign_media_distributions (a backend
    * virtual property), so the disciplines are matched here against the report's proposal.
    */
   private reportDisciplines = computed(() => this.intermediateReport()?.campaign_proposal?.media_distributions ?? []);

   /** Emits on every change of the KPI forms of this grid, so the container can derive the unsaved changes state */
   kpiFormChange = output<void>();

   localDataObject: Signal<Map<number, { interimValue: number }>> = computed(() => {
      const orders = this.orders() ?? [];
      const reportDate = this.reportDate();
      return untracked(() => {
         const dataMap = new Map<number, { interimValue: number }>();
         orders.forEach((order) => {
            const interimValue = this.getInterimFactor(reportDate, order.placement_date, order.end_date) * (primaryKpi(order)?.kpi_value?.value ?? 0);
            dataMap.set(order.id, { interimValue });
         });
         return dataMap;
      });
   });

   /** The specialists of the disciplines behind each order, a person or a team each; one holding several disciplines shows once. */
   orderSpecialists: Signal<Map<number, Array<TeamTarget>>> = computed(() => {
      const orders = this.orders() ?? [];
      const mediaDistributions = this.reportDisciplines();
      const specialistsByOrder = new Map<number, Array<TeamTarget>>();
      orders.forEach((order) => {
         const disciplines = untracked(() => findOrderMediaDistributions(order, mediaDistributions));
         const specialists = disciplines
            .map((discipline) => specialistOfDiscipline(discipline))
            .filter(isDefined)
            .filter((target, index, targets) => targets.findIndex((candidate) => sameTarget(candidate, target)) === index);
         specialistsByOrder.set(order.id, specialists);
      });
      return specialistsByOrder;
   });
   totals: WritableSignal<Totals | null> = signal(null);
   specialKpiSlugs = Object.keys(SPECIAL_KPIS); // The list of KPI slugs that should be calculated instead of entered by the user
   private destroySubject = new Subject(); // Subject to stop listening to old forms we might have replaced when orders or kpis are updated
   private allKpis = toSignal(this.lookupFacadeService.keyPerformanceIndicators$);
   SPECIAL_KPI_VALUE_TYPES = SPECIAL_KPI_VALUE_TYPES;

   /**
    * Per KPI slug: whether the rate cells stay empty. A special KPI is derived from other KPIs and has
    * no rate of its own; a KPI whose rate type is in {@link HIDDEN_RATE_TYPES} has one that means nothing.
    */
   protected rateHiddenBySlug = computed<Record<string, boolean>>(() =>
      Object.fromEntries(
         (this.allKpis() ?? []).map((kpi) => [kpi.slug, this.specialKpiSlugs.includes(kpi.slug) || HIDDEN_RATE_TYPES.includes(kpi.rate_type.slug)]),
      ),
   );

   orderKpiTotals = computed(() => {
      const orders = this.orders();
      const primaryKpiTotals: Record<string, { totalValue: number; interimValue: number }> = {};
      const reportDate = this.reportDate();
      // For each order that actually has a primary kpi, otherwise there's really no point
      return untracked(() => {
         orders.forEach((order) => {
            const primaryOrderKpi = primaryKpi(order);
            const primaryKpiSlug = primaryOrderKpi?.kpi?.slug;
            if (!primaryKpiSlug) {
               return;
            }
            const totalValue = (primaryKpiTotals[primaryKpiSlug]?.totalValue ?? 0) + (primaryOrderKpi?.kpi_value?.value ?? 0);
            // The interim values in the local data object are always only for the order's primary kpi, no need to recalculate
            const interimValue = (primaryKpiTotals[primaryKpiSlug]?.interimValue ?? 0) + (this.localDataObject().get(order.id)?.interimValue ?? 0);
            primaryKpiTotals[primaryKpiSlug] = { totalValue, interimValue };
            // seconday kpis?
            const interimFactor = order.secondary_kpis ? this.getInterimFactor(reportDate, order.placement_date, order.end_date) : 0;
            order.secondary_kpis?.forEach((secondaryKpi) => {
               // we filter out the special kpis (which are percentages) in the template, so don't worry about that now
               const kpiSlug = secondaryKpi.kpi?.slug;
               if (!kpiSlug) {
                  return;
               }
               const totalValue = (primaryKpiTotals[kpiSlug]?.totalValue ?? 0) + (secondaryKpi.kpi_value ?? 0);
               const interimValue = (primaryKpiTotals[kpiSlug]?.interimValue ?? 0) + (secondaryKpi.kpi_value ?? 0) * interimFactor;
               primaryKpiTotals[kpiSlug] = { totalValue, interimValue };
            });
         });
         return primaryKpiTotals;
      });
   });
   orderInvestmentTotal = computed(() => {
      // Total of the cost to client for all orders
      return this.orders().reduce((acc, curr) => acc + (curr.invoice_price?.value ?? 0), 0);
   });

   private dataChangeEffect = effect(() => {
      const formData = this.formDataMap();
      const orders = this.orders();
      const allKpis = this.allKpis();
      untracked(() => {
         if (formData && orders && allKpis?.length) {
            this.listenToFormChanges();
         }
      });
   });

   private readonlyEffect = effect(() => {
      const isReadonly = this.readonly();

      untracked(() => {
         this.formHelperService.readonly.set(isReadonly);

         for (const [orderId, orderData] of this.formDataMap().entries()) {
            const formArray = orderData.formGroup;
            if (this.formHelperService.editingDisabled()) {
               formArray.disable();
            } else {
               formArray.enable();
            }
         }
      });
   });

   private numberInputTemplate = viewChild<CellTemplate>('numberInput');
   private productCombinedTemplate = viewChild<CellTemplate>('productCombined');
   private intermediateValueTemplate = viewChild<CellTemplate>('intermediateValue');
   private menuTemplate = viewChild<CellTemplate>('menu');
   private rateTemplate = viewChild<CellTemplate>('rate');
   private kpiNameTemplate = viewChild<CellTemplate>('kpiName');
   private orderValueTemplate = viewChild<CellTemplate>('orderValue');
   private specialistTemplate = viewChild<CellTemplate>('specialist');
   private disciplineTemplate = viewChild<CellTemplate>('discipline');
   private targetingTemplate = viewChild<CellTemplate>('targeting');

   cellTemplates = computed<ComplexTableCellTemplates<IntermediateReportRow, KpiFormGroup>>(() => ({
      specialist: this.specialistTemplate(),
      product_combined: this.productCombinedTemplate(),
      order_value: this.orderValueTemplate(),
      kpi_investment: this.numberInputTemplate(),
      'kpi.name': this.kpiNameTemplate(),
      kpi_value: this.numberInputTemplate(),
      intermediate_value: this.intermediateValueTemplate(),
      rate: this.rateTemplate(),
      menu: this.menuTemplate(),
      discipline: this.disciplineTemplate(),
      targeting: this.targetingTemplate(),
   }));

   ngOnDestroy(): void {
      this.destroySubject.next(null);
   }

   private listenToFormChanges(): void {
      // Stop listening to the previous datamap form changes
      this.destroySubject.next(null);
      const localOrderIds = Array.from(this.localDataObject().keys());
      // Listen to valueChanges on all forms, and on every change recalculate the totals and set the values for the 'special' kpis
      const observables = Array.from(this.formDataMap().entries())
         .filter(([orderId]) => localOrderIds.includes(orderId))
         .map(([orderId, orderData]) =>
            orderData.formGroup.valueChanges.pipe(
               startWith(orderData.formGroup.getRawValue()),
               map((newFormValues) => ({ orderId, newFormValues })),
            ),
         );
      merge(...observables)
         .pipe(takeUntil(this.destroySubject))
         .subscribe({
            next: ({ orderId, newFormValues }) => {
               this.setSpecialKpiFormValues(orderId, newFormValues);
               this.setKpiTotals();
               this.kpiFormChange.emit();
               this.setRates(orderId, newFormValues);
            },
         });
   }

   private setRates(orderId: number, newFormValues: Array<KpiFormValue>) {
      const dataMap = this.formDataMap().get(orderId);
      if (!dataMap) {
         return;
      }
      // Get the primary KPI form for the investment value
      const firstFormControl = dataMap.formGroup.controls[0];
      newFormValues.forEach((newValue) => {
         const kpi = newValue.kpi;
         if (!kpi) {
            return;
         }
         const rate = ((firstFormControl.value.kpi_investment ?? 0) / (newValue.kpi_value ?? 0)) * kpi.rate_type.multiplier;
         const formControl = dataMap.formGroup.controls.find((control) => control.getRawValue().kpi.slug === kpi.slug);
         formControl?.patchValue({ rate }, { emitEvent: false });
      });
   }

   private setKpiTotals(): void {
      const kpisMap = new Map<string, number>();
      const localOrderIds = Array.from(this.localDataObject().keys());
      // Sum the investments from the first form of each order, but not the orders from potential other grid components
      const investment = Array.from(this.formDataMap().entries())
         .filter(([orderId]) => localOrderIds.includes(orderId))
         .reduce((acc, [_orderId, orderData]) => acc + (orderData.formGroup.getRawValue()?.at(0)?.kpi_investment ?? 0), 0);
      // Build the map of kpis and their total values for our orders
      this.formDataMap().forEach((orderData, orderId) => {
         if (localOrderIds.includes(orderId)) {
            orderData.formGroup.getRawValue().forEach((val) => {
               const currentTotalValue = (kpisMap.get(val.kpi.slug) ?? 0) + (val.kpi_value ?? 0);
               kpisMap.set(val.kpi.slug, currentTotalValue);
            });
         }
      });
      const kpiData = Array.from(kpisMap.entries()).flatMap(([kpislug, value]): Totals['kpiData'] => {
         // For the 'special' kpis the total is a percentage calculated from other kpis, they never have a rate value
         const rateType = this.allKpis()?.find((actualKpi) => actualKpi.slug === kpislug)?.rate_type;
         if (!rateType) {
            return [];
         }
         if (this.specialKpiSlugs.includes(kpislug)) {
            const calculatedValue = this.calculateSpecialKpi(kpislug, kpisMap);
            return [{ kpislug, value: calculatedValue, rateTypeName: rateType.name, rate: null }];
         } else {
            const multiplier = rateType.multiplier ?? 1;
            const rate = (investment / value) * multiplier;
            return [{ kpislug, value, rateTypeName: rateType.name, rate, decimalPrecision: rateType.decimal_precision }];
         }
      });
      this.totals.set({ kpiData, investment });
   }

   /**
    * Some formFields are disabled and not entered by the user, but instead calculated from other formfields
    */
   private setSpecialKpiFormValues(orderId: number, formValues: Array<KpiFormValue>): void {
      formValues.forEach((formValue) => {
         const kpiSlug = formValue.kpi?.slug;
         if (kpiSlug && this.specialKpiSlugs.includes(kpiSlug)) {
            const value = this.calculateSpecialKpi(kpiSlug, formValues);
            const formControl = this.formDataMap()
               .get(orderId)
               ?.formGroup.controls.find((control) => control.getRawValue().kpi.slug === kpiSlug);
            formControl?.patchValue({ kpi_value: value }, { emitEvent: false });
         }
      });
   }

   private calculateSpecialKpi(specialKpiSlug: string, formValues: Array<KpiFormValue> | Map<string, number>): number | null {
      const [slugA, slugB] = this.getCalcSlugs(specialKpiSlug);
      const valA = this.getValue(slugA, formValues);
      const valB = this.getValue(slugB, formValues);
      if (slugA && slugB && typeof valA === 'number' && typeof valB === 'number') {
         return valA / valB;
      }
      return null;
   }

   /**
    * Determines which other kpi values are used to calculate the special kpis
    */
   private getCalcSlugs(specialKpiSlug: string): Array<string> {
      const specialKpis: Partial<Record<string, Array<string>>> = SPECIAL_KPIS;
      return specialKpis[specialKpiSlug] ?? [];
   }

   private getValue(kpiSlug: string, formValues: Array<KpiFormValue> | Map<string, number>): number | null {
      if (formValues instanceof Map) {
         return formValues.get(kpiSlug) ?? null;
      }
      return formValues.find((value) => value?.kpi?.slug === kpiSlug)?.kpi_value ?? null;
   }

   async openKpiDialog(order: IntermediateReportRow): Promise<void> {
      if (this.formHelperService.editingDisabled()) {
         return;
      }
      const kpiData = this.formDataMap().get(order.id)?.formGroup.getRawValue();
      if (!kpiData) {
         return;
      }
      // Pass current form state as secondary_kpis (excluding the primary) so the dialog reflects pending edits rather than saved state.
      const secondaryKpisFromForm = kpiData
         .filter((entry) => !entry.is_primary_kpi)
         .map((entry) => ({ id: entry.id, kpi: entry.kpi, kpi_value: entry.kpi_value }));
      const primaryFromForm = kpiData.find((entry) => entry.is_primary_kpi);
      const selected = await this.secondaryKpiDialogHook(
         {
            id: order.id,
            nett_purchase_price: order.nett_purchase_price ?? 0,
            placement_date: order.placement_date,
            end_date: order.end_date,
            specifications: order.specifications,
            primary: primaryFromForm && {
               kpi: primaryFromForm.kpi,
               kpi_value: primaryFromForm.kpi_value ?? 0,
               purchase_rate_value: primaryKpi(order)?.purchase_rate_value?.value ?? 0,
            },
            secondary_kpis: secondaryKpisFromForm,
         },
         // Typecast (GER-4631): findInternalOrderCategory returns the raw main-category slug; the
         // dialog types the option as the CategoryGroupKey union. Mapping through
         // findCategoryGroupBySlug would change which KPIs match (they link to raw slugs).
         { currentDiscipline: findInternalOrderCategory(order) as CategoryGroupKey, persist: false, selectionOnly: true },
      );
      if (selected) {
         this.updateKpis(order.id, selected);
      }
   }

   /**
    * Update the order kpi data map with new and removed kpis
    */
   private updateKpis(orderId: number, selectedKpis: Array<SecondaryKpiEntry>): void {
      // find kpis that do not have a formgroup yet
      this.processNewKpis(orderId, selectedKpis);
      // filter the deselected kpis from the backend data
      this.processDeletedKpis(orderId, selectedKpis);
      // re-listen to form changes, deleting kpis does not get picked up by the totals otherwise
      this.listenToFormChanges();
   }

   /**
    * Find newly selected KPIs and return a new ActualKeyPerformanceIndicator object
    */
   private processNewKpis(orderId: number, selectedKpis: Array<SecondaryKpiEntry>): void {
      const formGroup = this.formDataMap().get(orderId)?.formGroup;
      if (!formGroup) {
         return;
      }
      // find kpis that do not have a formgroup yet
      const newKpis = selectedKpis.filter((selectedKpi) => !formGroup.controls.find((control) => control.getRawValue().kpi.slug === selectedKpi.kpi.slug));
      newKpis.forEach((newKpi) => {
         const kpiData = { order: { id: orderId }, kpi: newKpi?.kpi, kpi_value: null, kpi_investment: null, rate: null };
         const kpiForm = this.formHelperService.buildKpiFormGroup(kpiData);
         formGroup.push(kpiForm);
         kpiForm.markAsDirty();
      });
   }

   /**
    * Filter out KPIs that were deselected from the current backend data and return the list
    */
   private processDeletedKpis(orderId: number, selectedKpis: Array<SecondaryKpiEntry>): void {
      const selectedKpiSlugs = selectedKpis.map((newKpi) => newKpi.kpi.slug);
      const formData = this.formDataMap().get(orderId);
      if (!formData) {
         return;
      }
      const kpiValues = formData.formGroup.getRawValue();
      const rowOrderIds = formData.orders.map((order) => order.id);
      const existingKpis = this.actualKpiStore.entities();
      // and loop over the selected kpis to make a list of those that were removed and have an id since they should be deleted from the report when we save it
      kpiValues.forEach((kpiValue) => {
         if (kpiValue.is_primary_kpi) {
            // The dialog only manages secondary KPIs (the hook strips the primary from its result), so the
            // primary is never part of the selection and must never be treated as deleted here.
            return;
         }
         if (selectedKpiSlugs.includes(kpiValue.kpi.slug)) {
            // not a deleted kpi, nothing to do
            return;
         }
         // A bundle row covers several orders, so a deleted KPI must be removed from the saved record of
         // every order in the row (a normal row has a single order and yields at most one id). Resolve the
         // backend records straight from the store rather than the form, whose id only reflects one order.
         existingKpis
            .filter((existingKpi) => !!existingKpi.order && rowOrderIds.includes(existingKpi.order.id) && existingKpi.kpi?.slug === kpiValue.kpi.slug)
            .forEach((existingKpi) => this.saveLoadHelperService?.deleteIdsList.add(existingKpi.id));
         // Remove the form for the deleted kpi
         const formDataMapGroup = formData.formGroup;
         const index = formDataMapGroup.controls.findIndex((control) => control.getRawValue().kpi.slug === kpiValue.kpi.slug);
         formDataMapGroup.removeAt(index);
      });
   }

   private getInterimFactor(reportDate: Date, startDate?: Date | null, endDate?: Date | null): number {
      if (!startDate || !endDate || !reportDate) {
         return 0;
      }
      const campaignDays = differenceInCalendarDays(startDate, endDate);
      const daysInCampaign = differenceInCalendarDays(startDate, reportDate);
      // Return the multiply factor with a minimum of 0 and a maximum of 1
      return Math.max(0, Math.min(1, daysInCampaign / campaignDays));
   }
}
